import { Component, signal, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ManualEntryPayload } from '@model/patient/sample-entry.model';
import { EditSampleComponent } from '../edit-sample.component';
import { ReprocessFormComponent } from '../reprocess-form/reprocess-form.component';

type TabType = 'edit' | 'reprocess';

const TAB_EDIT: TabType = 'edit';
const TAB_REPROCESS: TabType = 'reprocess';

@Component({
  selector: 'app-edit-sample-tabs',
  standalone: true,
  imports: [CommonModule, EditSampleComponent, ReprocessFormComponent],
  template: `
    <div class="flex flex-col">
      <!-- Header con descripción -->
      <div class="mb-6">
        <p class="text-sm text-text-secondary m-0">
          Si tu muestra aún se encuentra en la zona de pre ingresos, en esta
          ventana puedes iterar cualquier campo que consideres necesario o
          notificar un reproceso
        </p>
      </div>

      <!-- Selector de tabs -->
      <div
        class="flex flex-row justify-center gap-12 mb-8 pb-6 border-b border-gray-200"
      >
        <div
          (click)="setActiveTab('edit')"
          (mouseenter)="hoveredTab.set('edit')"
          (mouseleave)="hoveredTab.set(null)"
          class="flex flex-col items-center justify-center gap-3 cursor-pointer transition-all"
        >
          <div
            class="rounded-full flex items-center justify-center transition-all"
            [ngClass]="{
              'w-16 h-16': !isEditTab(),
              'w-20 h-24': isEditTab(),
              'bg-yellow-custom': !isEditTab(),
              'bg-yellow-selected': isEditTab()
            }"
          >
            <i class="icon-barcode tab-icon"></i>
          </div>
          <p class="text-sm font-semibold text-brand-tertiary m-0">
            Editar datos
          </p>
          @if (showEditDescription()) {
            <p class="text-xs text-text-secondary text-center m-0">
              Edita los datos ingresados<br />de la muestra
            </p>
          }
        </div>

        <div
          (click)="setActiveTab('reprocess')"
          (mouseenter)="hoveredTab.set('reprocess')"
          (mouseleave)="hoveredTab.set(null)"
          class="flex flex-col items-center justify-center gap-3 cursor-pointer transition-all"
        >
          <div
            class="rounded-full flex items-center justify-center transition-all"
            [ngClass]="{
              'w-16 h-16': !isReprocessTab(),
              'w-20 h-24': isReprocessTab(),
              'bg-yellow-custom': !isReprocessTab(),
              'bg-yellow-selected': isReprocessTab()
            }"
          >
            <i class="icon-id-card tab-icon"></i>
          </div>
          <p class="text-sm font-semibold text-brand-tertiary m-0">
            Observaciones
          </p>
          @if (showReprocessDescription()) {
            <p class="text-xs text-text-secondary text-center m-0">
              Reporta, gestiona y edita<br />un reproceso
            </p>
          }
        </div>
      </div>

      <!-- Contenido con hidden para mantener componentes vivos -->
      <div class="flex-1 overflow-y-auto">
        <div [class.hidden]="!isEditTab()">
          <app-edit-sample
            [editData]="editData()"
            [caseNumber]="caseNumber()"
            (saveSuccess)="onSaveSuccess()"
            (cancelEdit)="onCancel()"
          ></app-edit-sample>
        </div>
        <div [class.hidden]="!isReprocessTab()">
          <app-reprocess-form
            [specimenCode]="caseNumber()"
            (saveSuccess)="onSaveSuccess()"
            (cancelClick)="onCancel()"
          ></app-reprocess-form>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['../../shared/styles/add-sample.component.scss'],
  styles: [
    `
      :host {
        display: block;
      }

      .hidden {
        display: none !important;
      }

      .tab-icon {
        width: 32px !important;
        height: 32px !important;
        display: inline-block;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
      }
    `
  ]
})
export class EditSampleTabsComponent {
  readonly editData = input.required<ManualEntryPayload>();
  readonly caseNumber = input.required<string>();
  readonly saveSuccess = output();
  readonly cancelEdit = output();

  activeTab = signal<TabType>(TAB_EDIT);
  hoveredTab = signal<TabType | null>(null);

  isEditTab = computed(() => this.activeTab() === TAB_EDIT);
  isReprocessTab = computed(() => this.activeTab() === TAB_REPROCESS);

  showEditDescription = computed(
    () => this.activeTab() === TAB_EDIT || this.hoveredTab() === TAB_EDIT
  );

  showReprocessDescription = computed(
    () =>
      this.activeTab() === TAB_REPROCESS || this.hoveredTab() === TAB_REPROCESS
  );

  setActiveTab(tab: TabType): void {
    this.activeTab.set(tab);
  }

  onSaveSuccess(): void {
    this.saveSuccess.emit();
  }

  onCancel(): void {
    this.cancelEdit.emit();
  }
}
